import React, {useEffect, useMemo, useRef} from "react";
import {createHashRouter, Navigate, useLocation} from "react-router-dom";
import {isClientRole, isOperatorRole, useAuth} from "@/app/auth/AuthContext";
import {RouteObject, RouterProvider} from "react-router";
import {LandingPage} from "@/pages/landing-page/LandingPage";
import {AppPaths} from "@/app/AppPaths";
import {resolveSignInRedirectTarget} from "@/app/auth/signInRedirect";
import {SignInPage} from "@/pages/login-page/AuthentificationPage";
import {CompanySignUpPage} from "@/pages/company-sign-up-page/CompanySignUpPage";
import InternalRequestPage from "@/pages/internal-request-page/InternalRequestPage";
import ExternalRequestPage from "@/pages/external-request-page/ExternalRequestPage";
import CompanyOverviewPage from "@/pages/company-overview-page/CompanyOverviewPage";
import CompaniesListPage from "@/pages/settings-companies/CompaniesListPage";
import CompanyPage from "@/pages/company-page/CompanyDetailsPage";
import UserPage from "@/pages/user-page/UserPage";
import {UserSignUpPage} from "@/pages/user-signup-page/UserSignUpPage";
import {PasswordResetPage} from "@/pages/user-password-reset-page/PasswordResetPage";
import PasswordResetRequestPage from "@/pages/user-password-reset-request-page/PasswordResetRequestPage";
import CompanyUnverifiedPage from "@/pages/company-overview-page/CompanyUnverifiedPage";
import {ImpressumPage} from "@/pages/info-pages/ImpressumPage";
import {PrivacyPage} from "@/pages/info-pages/PrivacyPage";
import {CookiePolicyPage} from "@/pages/info-pages/CookiePolicyPage";
import CartonGradesPage from "@/pages/carton-grades-page/CartonGradeImportPage";
import AddressAdminPage from "@/pages/address-admin-page/AddressAdminPage";
import ReportsPage from "@/pages/reports-admin-page/ReportsPage";
import BidderOverviewPage from "@/pages/views/BidderOverviewPage";
import RequesterOverviewPage from "@/pages/views/RequesterOverviewPage";
import LanguageFileManagementPage from "@/pages/language-files-page/LanguageFileManagementPage";
import OperatorViewPage from "@/pages/views/OperatorViewPage";

const RedirectToLogin = () => {
    const location = useLocation();
    const target = resolveSignInRedirectTarget(location.pathname, location.search, false);
    return <Navigate to={target} replace />;
};

export const AppRoutes = () => {
    const { authenticated, roles, environment } = useAuth();
    const isProd = environment?.toLowerCase() === "prod";
    const firstNav = useRef(true); // avoid double counting initial PV

    const router = useMemo(() => {
        const routes: RouteObject[] = [
            { path: AppPaths.LANDING_PAGE, element: <LandingPage /> },
            { path: AppPaths.IMPRESSUM_PAGE, element: <ImpressumPage /> },
            { path: AppPaths.PRIVACY_PAGE, element: <PrivacyPage /> },
            { path: AppPaths.COOKIE_POLICY_PAGE, element: <CookiePolicyPage /> },
            { path: AppPaths.SIGN_IN, element: <SignInPage /> },
            { path: AppPaths.COMPANY_SIGNUP, element: <CompanySignUpPage /> },
            { path: AppPaths.COMPANY, element: <CompanyPage /> },
            { path: AppPaths.SETTINGS_COMPANIES, element: <CompaniesListPage /> },
            { path: AppPaths.USER_SIGNUP, element: <UserSignUpPage /> },
            { path: AppPaths.USER_PASSWORD_RESET, element: <PasswordResetPage /> },
            { path: AppPaths.USER_PASSWORD_RESET_REQUEST, element: <PasswordResetRequestPage /> },
            { path: AppPaths.USER, element: <UserPage /> }
        ];
        if (authenticated) {
            if (isClientRole(roles)) {
                routes.push(
                    // New view routes
                    { path: AppPaths.BIDDER_OVERVIEW, element: <BidderOverviewPage /> },
                    { path: AppPaths.REQUESTER_OVERVIEW, element: <RequesterOverviewPage /> },
                    // Detail routes — unchanged
                    { path: AppPaths.EXTERNAL_REQUEST, element: <ExternalRequestPage /> },
                    { path: AppPaths.INTERNAL_REQUEST, element: <InternalRequestPage /> },
                    // Redirects for old list routes (bookmarks, email links)
                    {
                        path: AppPaths.EXTERNAL_REQUEST_OVERVIEW,
                        element: <Navigate to={AppPaths.BIDDER_OVERVIEW} replace />
                    },
                    {
                        path: AppPaths.EXTERNAL_ORDER_TRACKING,
                        element: <Navigate to={AppPaths.BIDDER_OVERVIEW} replace />
                    },
                    {
                        path: AppPaths.INTERNAL_REQUEST_OVERVIEW,
                        element: <Navigate to={AppPaths.REQUESTER_OVERVIEW} replace />
                    },
                    {
                        path: AppPaths.INTERNAL_ORDER_TRACKING,
                        element: <Navigate to={AppPaths.REQUESTER_OVERVIEW} replace />
                    },
                    // Catch-all → Bidder overview as default
                    { path: "*", element: <Navigate to={AppPaths.BIDDER_OVERVIEW} /> }
                );
            } else if (isOperatorRole(roles)) {
                routes.push(
                    { path: AppPaths.COMPANY_OVERVIEW, element: <CompanyOverviewPage /> },
                    { path: AppPaths.COMPANY_UNVERIFIED, element: <CompanyUnverifiedPage /> },
                    { path: AppPaths.CARTON_GRADES, element: <CartonGradesPage /> },
                    { path: AppPaths.ADMIN_ADDRESSES, element: <AddressAdminPage /> },
                    { path: AppPaths.LANGUAGE_FILES, element: <LanguageFileManagementPage /> },
                    { path: AppPaths.ADMIN_REPORTS, element: <ReportsPage /> },
                    // Start of the beta version
                    { path: AppPaths.OPERATOR_REQUEST_OVERVIEW, element: <OperatorViewPage /> },
                    { path: AppPaths.INTERNAL_REQUEST, element: <InternalRequestPage /> },
                    // End of the beta version
                    { path: "*", element: <Navigate to={AppPaths.COMPANY_OVERVIEW} /> }
                );
            } else {
                routes.push({ path: "*", element: <Navigate to="/" /> });
            }
        } else {
            routes.push({ path: "*", element: <RedirectToLogin /> });
        }

        return createHashRouter(routes);
    }, [authenticated, roles]);

    useEffect(() => {
        // Subscribe to navigation changes without changing your route tree
        const unsubscribe = router.subscribe((state) => {
            if (!isProd) {
                return;
            }

            // On first state we skip: initial page view is sent by existing useMatomoTracker
            if (firstNav.current) {
                firstNav.current = false;
                return;
            }

            // Queue calls (safe even if matomo.js not yet loaded)
            const w = window as any;
            const _paq = (w._paq = w._paq || []);
            const currentUrl = window.location.href; // includes # for hash router
            _paq.push([
                "setCustomUrl",
                currentUrl,
                "setDocumentTitle",
                document.title,
                "trackPageView",
                "enableLinkTracking"
            ]);
        });

        return () => unsubscribe();
    }, [router, isProd]);

    return <RouterProvider router={router} />;
};
